import { Component, HostListener, inject, AfterViewInit, OnDestroy, ElementRef, NgZone, ViewChild } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { HostnameService } from 'app/services/hostname.service';
import { AnalyticsService } from 'app/services/analytics.service';

@Component({
    selector: 'app-landing-v2',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './landing-v2.component.html',
    styleUrls: ['./landing-v2.component.scss']
})
export class LandingV2Component implements AfterViewInit, OnDestroy {
    private hostnameService = inject(HostnameService);
    private document = inject(DOCUMENT);
    private elRef = inject(ElementRef);
    private zone = inject(NgZone);
    private analyticsService = inject(AnalyticsService);

    navbarScrolled = false;
    mobileMenuOpen = false;
    activeFaq: number | null = null;

    @ViewChild('carouselTrack') carouselTrackRef!: ElementRef<HTMLElement>;

    readonly appLoginUrl = this.hostnameService.getAppUrl('/login');
    readonly appRegistroUrl = this.hostnameService.getAppUrl('/registro');

    // ── Feature cards (horizontal scroll) ──
    readonly features = [
        {
            icon: 'event_note',
            title: 'Agenda eletrônica',
            description: 'Organize seus atendimentos em um calendário intuitivo. Gerencie horários, clínicas e confirmações em uma só tela.',
            image: 'assets/img/ladingPage/Agenda.png',
        },
        {
            icon: 'description',
            title: 'Prontuário completo',
            description: 'Registre evoluções, formulários e documentos. Acompanhe o histórico do paciente em uma timeline visual.',
            image: 'assets/img/ladingPage/ProntuarioDesktop.png',
        },
        {
            icon: 'account_balance_wallet',
            title: 'Financeiro integrado',
            description: 'Controle receitas, despesas e repasses. Visualize métricas, inadimplência e a evolução da receita em um só painel.',
            image: 'assets/img/ladingPage/Financeiro_Painel.png',
        },
        {
            icon: 'chat',
            title: 'Lembretes automáticos',
            description: 'Configure lembretes por data para pacientes e tarefas internas. Na data marcada, o sistema notifica automaticamente.',
            image: 'assets/img/ladingPage/Lembretes.png',
        },
        {
            icon: 'assignment',
            title: 'Formulários personalizados',
            description: 'Crie anamneses e escalas sob medida para sua especialidade. Aplique direto no prontuário do paciente.',
            image: 'assets/img/ladingPage/Formulario.png',
        },
        {
            icon: 'receipt_long',
            title: 'Controle de despesas',
            description: 'Categorize despesas, visualize a distribuição por tipo e acompanhe próximos vencimentos e inadimplentes.',
            image: 'assets/img/ladingPage/FinanceiroPainel_2.png',
        },
        {
            icon: 'insights',
            title: 'Dashboard de resultados',
            description: 'Acompanhe atendimentos, receita e horários mais procurados em gráficos atualizados em tempo real.',
            image: 'assets/img/ladingPage/Dashboard.png',
        },
        {
            icon: 'location_city',
            title: 'Multi-clínicas',
            description: 'Atende em mais de um local? Gerencie todas as suas clínicas e agendas em uma única conta.',
            image: 'assets/img/ladingPage/MultiClinicas.png',
        },
    ];

    // ── Stats / metrics ──
    readonly stats = [
        { value: 0, target: 1500, suffix: '+', label: 'Atendimentos registrados' },
        { value: 0, target: 300, suffix: '+', label: 'Evoluções cadastradas' },
        { value: 0, target: 50, suffix: '+', label: 'Prontuários criados' },
        { value: 0, target: 2500, suffix: '+', label: 'Lembretes enviados por WhatsApp' },
    ];

    readonly planFeatures = [
        { label: 'Agenda ilimitada', icon: 'event_note' },
        { label: 'Clientes ilimitados', icon: 'group' },
        { label: 'Prontuário com timeline', icon: 'description' },
        { label: 'Evoluções clínicas', icon: 'trending_up' },
        { label: 'Formulários personalizados', icon: 'assignment' },
        { label: 'Financeiro completo', icon: 'account_balance_wallet' },
        { label: 'Gestão de convênios', icon: 'health_and_safety' },
        { label: 'Multi-clínicas', icon: 'location_city' },
        { label: 'Dashboard com gráficos', icon: 'insights' },
        { label: 'Lembretes via WhatsApp', icon: 'chat' },
        { label: 'Acesso mobile', icon: 'phone_iphone' },
    ];

    readonly testimonials = [
        { quote: 'Reduzi meu tempo com organização em mais de 50%. Agora foco no que importa: meus pacientes.', author: 'Psicóloga', initials: 'PS' },
        { quote: 'Agora consigo ver tudo do paciente em segundos. A timeline mudou minha rotina.', author: 'Fisioterapeuta', initials: 'FT' },
        { quote: 'Finalmente um sistema que não parece sistema médico. Simples e direto.', author: 'Nutricionista', initials: 'NT' },
    ];

    readonly faqs = [
        { question: 'Preciso instalar algo?', answer: 'Não. O Easy Consultório funciona direto no navegador. Acesse pelo computador, tablet ou celular — sem downloads.' },
        { question: 'Meus dados são seguros?', answer: 'Sim. Todos os dados são armazenados na nuvem com criptografia e backups automáticos diários.' },
        { question: 'O que acontece após os 15 dias?', answer: 'Você escolhe se quer assinar o plano completo por R$ 59/mês. Sem surpresas, sem cobrança automática.' },
        { question: 'Posso cancelar quando quiser?', answer: 'Sim. Não há contratos de fidelidade. Cancele a qualquer momento, sem multas.' },
    ];

    // ── Observers ──
    private revealObserver!: IntersectionObserver;
    private staggerObserver!: IntersectionObserver;
    private counterObserver!: IntersectionObserver;
    private parallaxRaf = 0;

    ngAfterViewInit() {
        this.zone.runOutsideAngular(() => {
            this.setupRevealObserver();
            this.setupStaggerObserver();
            this.setupCounterObserver();
            this.setupParallax();
        });
    }

    ngOnDestroy() {
        this.revealObserver?.disconnect();
        this.staggerObserver?.disconnect();
        this.counterObserver?.disconnect();
        if (this.parallaxRaf) cancelAnimationFrame(this.parallaxRaf);
    }

    // ── Scroll-reveal (single elements) ──
    private setupRevealObserver() {
        this.revealObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('revealed');
                        this.revealObserver.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.1, rootMargin: '0px 0px -60px 0px' }
        );

        const els = this.elRef.nativeElement.querySelectorAll('.reveal');
        els.forEach((el: Element) => this.revealObserver.observe(el));
    }

    // ── Stagger-reveal (children appear sequentially) ──
    private setupStaggerObserver() {
        this.staggerObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const children = entry.target.querySelectorAll('.stagger-item');
                        children.forEach((child: Element, i: number) => {
                            (child as HTMLElement).style.transitionDelay = `${i * 120}ms`;
                            child.classList.add('stagger-visible');
                        });
                        this.staggerObserver.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
        );

        const groups = this.elRef.nativeElement.querySelectorAll('.stagger-group');
        groups.forEach((el: Element) => this.staggerObserver.observe(el));
    }

    // ── Counter animation ──
    private setupCounterObserver() {
        this.counterObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        this.animateCounters();
                        this.counterObserver.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.3 }
        );

        const statsEl = this.elRef.nativeElement.querySelector('.stats');
        if (statsEl) this.counterObserver.observe(statsEl);
    }

    private animateCounters() {
        const duration = 2000;
        const start = performance.now();

        const easeOutExpo = (t: number) => t === 1 ? 1 : 1 - Math.pow(2, -10 * t);

        const tick = (now: number) => {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            const eased = easeOutExpo(progress);

            this.zone.run(() => {
                this.stats.forEach(stat => {
                    stat.value = Math.round(eased * stat.target);
                });
            });

            if (progress < 1) {
                requestAnimationFrame(tick);
            }
        };
        requestAnimationFrame(tick);
    }

    // ── Parallax on hero orbs ──
    private setupParallax() {
        const orbs = this.elRef.nativeElement.querySelectorAll('.hero__orb') as NodeListOf<HTMLElement>;
        const heroEl = this.elRef.nativeElement.querySelector('.hero') as HTMLElement;
        if (!heroEl || orbs.length === 0) return;

        const speeds = [0.04, 0.06, 0.03];
        let lastScroll = -1;

        const onScroll = () => {
            const scrollY = window.scrollY;
            if (scrollY === lastScroll) { this.parallaxRaf = requestAnimationFrame(onScroll); return; }
            lastScroll = scrollY;

            const heroBottom = heroEl.offsetTop + heroEl.offsetHeight;
            if (scrollY < heroBottom) {
                orbs.forEach((orb, i) => {
                    const speed = speeds[i] || 0.04;
                    orb.style.transform = `translateY(${scrollY * speed * -1}px)`;
                });
            }
            this.parallaxRaf = requestAnimationFrame(onScroll);
        };
        this.parallaxRaf = requestAnimationFrame(onScroll);
    }

    scrollCarousel(direction: -1 | 1) {
        const track = this.carouselTrackRef?.nativeElement;
        if (!track) return;
        const cardWidth = 340 + 28; // card width + gap
        track.scrollBy({ left: direction * cardWidth * 2, behavior: 'smooth' });
    }

    @HostListener('window:scroll')
    onWindowScroll() {
        this.navbarScrolled = window.scrollY > 50;
    }

    toggleMobileMenu() {
        this.mobileMenuOpen = !this.mobileMenuOpen;
    }

    toggleFaq(index: number) {
        const isOpening = this.activeFaq !== index;
        this.activeFaq = isOpening ? index : null;
        if (isOpening) {
            this.analyticsService.trackEvent('faq_interaction', { question: this.faqs[index]?.question ?? '' });
        }
    }

    trackCta(label: string) {
        this.analyticsService.trackEvent('cta_click', { label });
    }

    scrollTo(sectionId: string, event: Event) {
        event.preventDefault();
        this.mobileMenuOpen = false;
        const el = this.document.getElementById(sectionId);
        if (el) {
            const offset = 80;
            const y = el.getBoundingClientRect().top + window.scrollY - offset;
            window.scrollTo({ top: y, behavior: 'smooth' });
        }
    }
}
